import React, { ChangeEvent, useRef, useState } from 'react';
import { Alert, Button, styled, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import Appbar from 'components/appbar/Appbar';
import InsideSaving from 'components/loading/InsideSaving';
import PageHeaderActions from 'components/page-header/PageHeaderActions';
import { exportToExcel } from 'helpers/exportToExcel';
import { api } from 'services/api';
import history from 'services/history';
import { BudgetImportReport } from 'types/budgetImport';
import ImportReport from './ImportReport';
import { parseBudgetWorkbook } from './parseBudgetWorkbook';

const Content = styled('div')({ display: 'flex', flexDirection: 'column', gap: 24 });

const SurgerieImport: React.FC = () => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [checking, setChecking] = useState(false);
  const [fileName, setFileName] = useState('');
  const [packageRows, setPackageRows] = useState(0);
  const [report, setReport] = useState<BudgetImportReport | null>(null);

  function goBack() {
    history.push('/surgeries/closed');
  }

  async function handleFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setChecking(true);
    setReport(null);
    setFileName(file.name);

    try {
      const parsed = await parseBudgetWorkbook(file);
      if (!parsed.rows.length) throw new Error('A aba "Orçamentos" não tem nenhum orçamento preenchido.');

      setPackageRows(parsed.packageRows);
      const response = await api.post<BudgetImportReport>('/budgets/import/validate', { rows: parsed.rows });
      setReport(response.data);
    } catch (err: any) {
      toast.error(err.response?.data?.error || err.message || 'Não foi possível validar a planilha');
    } finally {
      setChecking(false);
    }
  }

  function handleExport() {
    if (!report) return;

    exportToExcel({
      fileName: 'validacao-importacao-orcamentos',
      sheetName: 'Validação',
      columns: [
        { header: 'Nº', width: 60 },
        { header: 'Paciente', width: 260 },
        { header: 'Procedimento', width: 280 },
        { header: 'Venda', width: 110, align: 'right' },
        { header: 'Situação', width: 90 },
        { header: 'Detalhes', width: 600 },
      ],
      rows: report.rows.map(row => [
        { value: row.number },
        { value: row.patient_name },
        { value: row.procedure },
        { value: row.value_sale, money: true },
        { value: { ok: 'Ok', warning: 'Aviso', error: 'Erro' }[row.status] },
        { value: row.issues.map(issue => issue.message).join(' | ') },
      ]),
    });
  }

  return (
    <>
      <Appbar title="Importar orçamentos" backAction={goBack} />
      {checking && <InsideSaving message="Conferindo a planilha..." />}

      <PageHeaderActions
        title="Importar orçamentos fechados"
        description="Envie a planilha preenchida para conferir antes de importar."
        ActionComponent={
          <div style={{ display: 'flex', gap: 8 }}>
            {report && (
              <Button variant="outlined" color="inherit" onClick={handleExport}>
                Baixar relatório
              </Button>
            )}
            <Button variant="contained" color="primary" onClick={() => inputRef.current?.click()}>
              {report ? 'Enviar outra planilha' : 'Enviar planilha'}
            </Button>
          </div>
        }
      />

      <input ref={inputRef} type="file" accept=".xlsx" hidden onChange={handleFile} />

      <Content>
        <Alert severity="info">
          Esta etapa só confere a planilha com o cadastro. Nada é gravado, e a importação ainda não está liberada.
        </Alert>

        {!report && !checking && (
          <Typography color="textSecondary">
            Use o modelo de importação de orçamentos. Só a aba &quot;Orçamentos&quot; é conferida por enquanto.
          </Typography>
        )}

        {report && (
          <>
            <Typography variant="body2" color="textSecondary">
              Arquivo: {fileName}
            </Typography>
            {packageRows > 0 && (
              <Alert severity="warning">
                A aba &quot;Pacotes do orçamento&quot; tem {packageRows} linha(s), que ainda não são conferidas.
              </Alert>
            )}
            <ImportReport report={report} />
          </>
        )}
      </Content>
    </>
  );
};

export default SurgerieImport;
